import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, ComposedChart,
  RadialBarChart, RadialBar
} from 'recharts';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiShoppingBag,
  FiDollarSign,
  FiUsers,
  FiRepeat,
  FiShoppingCart,
  FiActivity
} from 'react-icons/fi';
import api from '../../api/api';
import { toast } from 'react-hot-toast';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    averageOrderValue: 0,
    customerRetention: 0,
    topSellingCategories: [],
    salesTrends: [],
    customerSegments: [],
    inventoryMetrics: [],
    popularProducts: []
  });
  const [revenueData, setRevenueData] = useState([]);
  const [inventoryStats, setInventoryStats] = useState({
    totalProducts: 0,
    totalStock: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    categoryBreakdown: [],
    stockDistribution: []
  });

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    })}`;
  };

  // Process revenue data for the selected time range
  const processRevenueData = (orders, timeRange) => {
    // Initialize all months with 0 values
    const monthsOrder = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const initialMonths = monthsOrder.reduce((acc, month) => {
      acc[month] = { month, revenue: 0, orders: 0 };
      return acc;
    }, {});

    // Get date range based on selected timeRange
    const now = new Date();
    let startDate;
    switch (timeRange) {
      case '7d':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case '30d':
        startDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case '90d':
        startDate = new Date(now.setDate(now.getDate() - 90));
        break;
      case '1y':
      default:
        startDate = new Date(now.getFullYear(), 0, 1); // Start of current year
        break;
    }

    // Filter and process orders
    const relevantOrders = orders.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= startDate && 
             (order.payment_status?.toLowerCase() === 'completed' || 
              order.paymentStatus?.toLowerCase() === 'completed');
    });

    // Aggregate orders by month
    const aggregatedData = relevantOrders.reduce((acc, order) => {
      const orderDate = new Date(order.created_at);
      const month = orderDate.toLocaleString('default', { month: 'short' });
      const amount = parseFloat(order.total_amount || order.total || 0);

      acc[month].revenue += amount;
      acc[month].orders += 1;
      return acc;
    }, initialMonths);

    // Convert to array and maintain month order
    return monthsOrder.map(month => ({
      month,
      revenue: aggregatedData[month].revenue,
      orders: aggregatedData[month].orders,
      average: aggregatedData[month].orders > 0 
        ? aggregatedData[month].revenue / aggregatedData[month].orders 
        : 0
    }));
  };

  const processInventoryData = (products, categories) => {
    // Filter only active categories
    const activeCategories = categories.filter(category => category.is_active);
    
    const totalProducts = products.length;
    let totalStock = 0;
    let lowStockCount = 0;
    let outOfStockCount = 0;
    
    // Process product stock levels (only for products in active categories)
    products.forEach(product => {
      // Only process products that belong to active categories
      if (activeCategories.some(cat => cat.id === product.category?.id)) {
        const productStock = product.variants?.reduce((sum, variant) => {
          const stock = parseInt(variant.stock) || 0;
          return sum + stock;
        }, 0) || 0;

        totalStock += productStock;
        
        if (productStock === 0) {
          outOfStockCount++;
        } else if (productStock <= 5) {
          lowStockCount++;
        }
      }
    });

    // Process category breakdown with detailed metrics (only active categories)
    const categoryBreakdown = activeCategories.map(category => {
      const categoryProducts = products.filter(p => p.category?.id === category.id);
      const totalCategoryStock = categoryProducts.reduce((sum, product) => {
        return sum + (product.variants?.reduce((variantSum, variant) => 
          variantSum + (parseInt(variant.stock) || 0), 0) || 0);
      }, 0);

      const stockLevels = {
        inStock: categoryProducts.filter(p => {
          const stock = p.variants?.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0) || 0;
          return stock > 10;
        }).length,
        lowStock: categoryProducts.filter(p => {
          const stock = p.variants?.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0) || 0;
          return stock > 0 && stock <= 10;
        }).length,
        outOfStock: categoryProducts.filter(p => {
          const stock = p.variants?.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0) || 0;
          return stock === 0;
        }).length
      };

      return {
        name: category.name,
        totalProducts: categoryProducts.length,
        totalStock: totalCategoryStock,
        ...stockLevels,
        stockPercentage: totalStock > 0 ? (totalCategoryStock / totalStock * 100).toFixed(1) : 0
      };
    });

    // Filter out categories with no products
    const activeBreakdown = categoryBreakdown.filter(category => category.totalProducts > 0);

    // Create stock distribution data for RadialBar chart
    const stockDistribution = [
      {
        name: 'Well Stocked',
        value: totalProducts > 0 ? ((totalProducts - lowStockCount - outOfStockCount) / totalProducts * 100).toFixed(1) : 0,
        fill: '#10B981'
      },
      {
        name: 'Low Stock',
        value: totalProducts > 0 ? (lowStockCount / totalProducts * 100).toFixed(1) : 0,
        fill: '#F59E0B'
      },
      {
        name: 'Out of Stock',
        value: totalProducts > 0 ? (outOfStockCount / totalProducts * 100).toFixed(1) : 0,
        fill: '#EF4444'
      }
    ];

    return {
      totalProducts,
      totalStock,
      lowStockCount,
      outOfStockCount,
      categoryBreakdown: activeBreakdown,
      stockDistribution
    };
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    setIsLoading(true);
    try {
      const [orders, products, categories] = await Promise.all([
        api.getAllOrders(),
        api.getProducts(),
        api.getCategories()
      ]);

      // Process inventory data
      const inventoryData = processInventoryData(products, categories);
      setInventoryStats(inventoryData);

      // Filter completed orders
      const completedOrders = orders.filter(order => 
        order.payment_status?.toLowerCase() === 'completed' || 
        order.paymentStatus?.toLowerCase() === 'completed'
      );

      // Process revenue data with completed orders only
      const processedRevenueData = processRevenueData(completedOrders, timeRange);
      setRevenueData(processedRevenueData);

      // Calculate total revenue from completed orders only
      const totalRevenue = completedOrders.reduce((sum, order) => 
        sum + parseFloat(order.total_amount || order.total || 0), 0
      );

      const totalOrders = completedOrders.length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      // Process sales trends (group by month)
      const salesByMonth = completedOrders.reduce((acc, order) => {
        const date = new Date(order.created_at);
        const month = date.toLocaleString('default', { month: 'short' });
        if (!acc[month]) {
          acc[month] = { revenue: 0, orders: 0 };
        }
        acc[month].revenue += parseFloat(order.total_amount || order.total || 0);
        acc[month].orders += 1;
        return acc;
      }, {});

      const salesTrends = Object.entries(salesByMonth).map(([month, data]) => ({
        month,
        revenue: data.revenue,
        orders: data.orders
      }));

      // Process inventory metrics
      const inventoryByCategory = categories.map(category => {
        const categoryProducts = products.filter(p => p.category.id === category.id);
        const inStock = categoryProducts.filter(p => {
          const totalStock = p.variants?.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0) || 0;
          return totalStock > 10;
        }).length;
        const lowStock = categoryProducts.filter(p => {
          const totalStock = p.variants?.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0) || 0;
          return totalStock > 0 && totalStock <= 10;
        }).length;
        const outOfStock = categoryProducts.filter(p => {
          const totalStock = p.variants?.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0) || 0;
          return totalStock === 0;
        }).length;

        return {
          category: category.name,
          inStock,
          lowStock,
          outOfStock
        };
      });

      // Calculate customer retention
      const calculateCustomerRetention = (orders) => {
        // Group orders by customer ID
        const customerOrders = orders.reduce((acc, order) => {
          const userId = order.user_id;
          if (!acc[userId]) {
            acc[userId] = [];
          }
          acc[userId].push({
            date: new Date(order.created_at),
            amount: parseFloat(order.total_amount || order.total || 0)
          });
          return acc;
        }, {});

        // Get total number of customers
        const totalCustomers = Object.keys(customerOrders).length;
        
        // Count customers with more than one order
        const repeatCustomers = Object.values(customerOrders).filter(orders => orders.length > 1).length;
        
        // Calculate retention rate
        const retentionRate = totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;
        
        return {
          totalCustomers,
          repeatCustomers,
          retentionRate
        };
      };

      const customerRetentionMetrics = calculateCustomerRetention(completedOrders);

      setMetrics({
        totalRevenue,
        averageOrderValue,
        customerRetention: customerRetentionMetrics.retentionRate,
        customerMetrics: {
          totalCustomers: customerRetentionMetrics.totalCustomers,
          repeatCustomers: customerRetentionMetrics.repeatCustomers
        },
        salesTrends,
        inventoryMetrics: inventoryByCategory,
      });

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

  const MetricCard = ({ title, value, icon: Icon, trend, isPositive, subtext }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
            <Icon className={`w-6 h-6 ${isPositive ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <h3 className="text-2xl font-semibold mt-1">{value}</h3>
            {subtext && <p className="text-sm text-gray-500">{subtext}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <div className="flex justify-center items-center h-full">Loading analytics...</div>;
  }

  // Revenue Trends Chart Component
  const RevenueTrendsChart = ({ data }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Revenue Trends</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Orders</span>
          </div>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.1}/>
                <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis 
              dataKey="month" 
              tick={{ fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis 
              yAxisId="left"
              tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
              tick={{ fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              dataKey="orders"
              tick={{ fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <Tooltip content={CustomTooltip} />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              stroke="#4F46E5"
              strokeWidth={2}
              fill="url(#revenueGradient)"
              dot={{ fill: '#4F46E5', strokeWidth: 2 }}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="orders"
              stroke="#10B981"
              strokeWidth={2}
              dot={{ fill: '#10B981', strokeWidth: 2 }}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-100">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-sm text-indigo-600">
              Revenue: {formatCurrency(payload[0].value)}
            </p>
            <p className="text-sm text-green-600">
              Orders: {payload[1].value}
            </p>
            <p className="text-sm text-gray-600">
              Avg. Order: {formatCurrency(payload[0].value / payload[1].value || 0)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const InventoryStatus = ({ data }) => (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Inventory Status</h2>
        <span className="text-sm text-gray-500">
          Showing {data.categoryBreakdown.length} Active Categories
        </span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Summary Stats */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-indigo-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Total Products</p>
              <p className="text-2xl font-semibold text-indigo-600">{data.totalProducts}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Total Stock</p>
              <p className="text-2xl font-semibold text-green-600">{data.totalStock}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-semibold text-yellow-600">{data.lowStockCount}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">Out of Stock</p>
              <p className="text-2xl font-semibold text-red-600">{data.outOfStockCount}</p>
            </div>
          </div>

          {/* Stock Distribution Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                innerRadius="30%"
                outerRadius="100%"
                data={data.stockDistribution}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar
                  minAngle={15}
                  background
                  clockWise={true}
                  dataKey="value"
                  cornerRadius={10}
                />
                <Legend
                  iconSize={10}
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Percentage']}
                />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Breakdown - Only Active Categories */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-700">Active Categories Breakdown</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {data.categoryBreakdown.map((category, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">{category.name}</span>
                  <span className="text-sm text-gray-500">{category.stockPercentage}% of total</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="text-green-600">
                    In Stock: {category.inStock}
                  </div>
                  <div className="text-yellow-600">
                    Low: {category.lowStock}
                  </div>
                  <div className="text-red-600">
                    Out: {category.outOfStock}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500"
                    style={{ width: `${(category.inStock / category.totalProducts) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <Helmet>
        <title>Analytics | Admin | Panchali Boutique</title>
      </Helmet>



      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <MetricCard
          title="Total Revenue"
          value={`₹${metrics.totalRevenue.toLocaleString()}`}
          icon={FiDollarSign}
          isPositive={true}
        />
        <MetricCard
          title="Average Order Value"
          value={`₹${metrics.averageOrderValue.toLocaleString()}`}
          icon={FiShoppingCart}
          isPositive={true}
        />
        <MetricCard
          title="Customer Retention"
          value={`${metrics.customerRetention.toFixed(1)}%`}
          subtext={`${metrics.customerMetrics.repeatCustomers} repeat customers`}
          icon={FiRepeat}
          isPositive={metrics.customerRetention > 0}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trends */}
        <RevenueTrendsChart data={revenueData} />

        {/* Inventory Status */}
        <InventoryStatus data={inventoryStats} />
      </div>
    </motion.div>
  );
};

export default Analytics;










